// @ts-nocheck
import { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  Form,
  Button,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  ArrowsCollapse,
  ArrowsExpand,
  FilePdf,
  ChevronUp,
  ChevronDown,
  InfoCircleFill,
} from "react-bootstrap-icons";
import { NumericFormat } from "react-number-format";
import "./DescricaoDespesa.css";
import Correcao from "../../../Componentes/CorrecaoComponente/Correcao";
import CustomSelect from "../../../../../components/CustomSelect/CustomSelect";
import {
  downloadDespesaByIdentificador,
  downloadArquivoDespesaByArquivoId,
  TIPO_DESPESA_OUTROS_ID,
} from "../../../../../services/FinanceiroService";
import { FaEdit, FaDownload, FaTrash, FaFolderOpen } from "react-icons/fa";
import NotificationToast from "../../../../../components/NotificationToast/NotificationToast";

const MAX_PDF_SIZE_MB = 30;
const MAX_PDF_SIZE_BYTES = MAX_PDF_SIZE_MB * 1024 * 1024;

const uuid = () =>
  ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, (c) =>
    (
      c ^
      (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (c / 4)))
    ).toString(16),
  );

const compareTextPtBR = (a, b) => {
  const sa = String(a ?? "").trim();
  const sb = String(b ?? "").trim();
  return sa.localeCompare(sb, "pt-BR", { sensitivity: "base" });
};

const isTipoOutros = (tipoId, label) => {
  if (Number(tipoId) === TIPO_DESPESA_OUTROS_ID) return true;
  return (
    String(label || "")
      .trim()
      .toLowerCase() === "outros"
  );
};

function DescricaoDespesa({
  TiposDespesas,
  DadosDespesas,
  onChange,
  reloadKey,
  errors = {},
  errorsDetalhe = {},
  onCorrecaoChange,
  correcoes = {},
  errosCorrecoes = {},
  isSindicato = true,
  bloquearEdicao = false,
  idsParcelasSolicitando = new Set(),
  hasSolicitando = false,
  /** V2: chamado ao anexar PDF — persiste via POST arquivo-despesa. */
  onUploadArquivo = null,
  /** V2: chamado ao remover despesa já persistida (com id). */
  onDeleteDespesa = null,
}) {
  const [novaDespesa, setNovaDespesa] = useState("");
  const [novaDescricaoOutros, setNovaDescricaoOutros] = useState("");
  const [erroNovaDescricaoOutros, setErroNovaDescricaoOutros] = useState(false);
  const [despesas, setDespesas] = useState([]);
  const initializedRef = useRef(false);

  const opcoes = useMemo(
    () =>
      (TiposDespesas || []).map((t) => ({
        value: t.id,
        label: t.descricao,
      })),
    [TiposDespesas],
  );

  const outrosOption = useMemo(
    () =>
      opcoes.find((o) => isTipoOutros(o.value, o.label)) || null,
    [opcoes],
  );
  const OUTROS_VALUE = outrosOption?.value ?? null;

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("error");
  const [uploadingId, setUploadingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const notify = useCallback((message, type = "error") => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
  }, []);

  useEffect(() => {
    if (initializedRef.current && !reloadKey) return;

    const origemLocal = Array.isArray(DadosDespesas)
      ? DadosDespesas
      : DadosDespesas?.comprovantesDespesas || [];

    const normalizar = (item) => {
      const idTipo =
        item.idTipoDespesa ?? item.tipoDespesaId ?? item.idTipo ?? null;

      const labelTipo =
        item.descricao || opcoes.find((o) => o.value === idTipo)?.label || "";

      return {
        descricao: labelTipo,
        historicoAnaliseAdequacaoDespesa: item.historicoAnaliseAdequacaoDespesa,
        idTipoDespesa: idTipo || "",
        identificador: item.identificador ?? uuid(),
        nomeComprovante: item.nomeComprovante || "",
        valor:
          typeof item.valor === "number" ? item.valor : Number(item.valor) || 0,
        file: item.file
          ? item.file
          : item.nomeArquivo
            ? { name: item.nomeArquivo, base64: null }
            : item.nomeComprovante
              ? { name: item.nomeComprovante, base64: null }
              : null,
        nova: item.nova === true,
        deletar: Boolean(item.deletar) || false,
        collapsed: Boolean(item.collapsed) || false,
        primeiraParcela: item.primeiraParcela ?? item.PrimeiraParcela ?? false,
        segundaParcela: item.segundaParcela ?? item.SegundaParcela ?? false,
        id: item.id ?? item.idComprovanteDespesa ?? undefined,
        nomeArquivo: item.nomeArquivo ?? "",
        arquivoId: item.arquivoId ?? item.ArquivoId ?? null,
      };
    };

    if (origemLocal && origemLocal.length > 0) {
      setDespesas(origemLocal.map(normalizar));
      initializedRef.current = true;
    } else {
      setDespesas([]);
      initializedRef.current = true;
    }
  }, [DadosDespesas, opcoes, reloadKey]);

  useEffect(() => {
    if (!initializedRef.current) return;
    setDespesas((prev) =>
      prev.map((d) => ({
        ...d,
        descricao:
          d.descricao ||
          opcoes.find((o) => o.value === d.idTipoDespesa)?.label ||
          d.descricao,
      })),
    );
  }, [opcoes]);

  useEffect(() => {
    onChange?.(despesas);
  }, [despesas, onChange]);

  const hasPendenciaCorrecao = useCallback((d) => {
    const hist = Array.isArray(d?.historicoAnaliseAdequacaoDespesa)
      ? d.historicoAnaliseAdequacaoDespesa
      : [];
    return hist.some(
      (h) =>
        h?.dataSolicitacao != null &&
        String(h.dataSolicitacao).trim() !== "" &&
        h?.conforme === false &&
        (h?.dataCorrecao == null || h.dataCorrecao === ""),
    );
  }, []);

  const éNovo = (d) => d?.nova === true;

  const emModoCorrecao = useMemo(
    () => (despesas || []).some(hasPendenciaCorrecao),
    [despesas, hasPendenciaCorrecao],
  );

  const podeEditarItem = (d) => {
    if (!d || d.deletar) return false;
    if (bloquearEdicao) return false;
    if (éNovo(d)) return true;
    if (hasPendenciaCorrecao(d)) return true;
    if (!emModoCorrecao) return true;
    return false;
  };

  const deveOcultarPorSolicitando = useCallback(
    (d) => {
      if (hasPendenciaCorrecao(d)) return false;

      const p1 = d?.primeiraParcela === true;
      const p2 = d?.segundaParcela === true;

      if (!p1 && !p2) return false;

      if (p1 && idsParcelasSolicitando.has(1)) return true;
      if (p2 && idsParcelasSolicitando.has(2)) return true;

      return false;
    },
    [hasPendenciaCorrecao, idsParcelasSolicitando],
  );

  const ativos = useMemo(() => despesas.filter((d) => !d.deletar), [despesas]);

  const ativosVisiveis = useMemo(
    () => ativos.filter((d) => !deveOcultarPorSolicitando(d)),
    [ativos, deveOcultarPorSolicitando],
  );

  const ativosVisiveisOrdenados = useMemo(() => {
    const arr = [...ativosVisiveis];
    arr.sort((a, b) => {
      const byDesc = compareTextPtBR(a?.descricao, b?.descricao);
      if (byDesc !== 0) return byDesc;
      const byTipo = compareTextPtBR(a?.idTipoDespesa, b?.idTipoDespesa);
      if (byTipo !== 0) return byTipo;

      return compareTextPtBR(a?.identificador, b?.identificador);
    });
    return arr;
  }, [ativosVisiveis]);

  const tudoColapsado = useMemo(
    () => ativosVisiveisOrdenados.every((item) => item.collapsed),
    [ativosVisiveisOrdenados],
  );

  const opcoesParaAdicionar = useMemo(() => {
    // Regra: 1 despesa por tipo (exceto Outros, que pode repetir)
    const idsUsadosVisiveis = new Set(
      ativosVisiveisOrdenados.map((item) => Number(item.idTipoDespesa)),
    );
    return opcoes.filter((op) => {
      if (isTipoOutros(op.value, op.label)) return true;
      return !idsUsadosVisiveis.has(Number(op.value));
    });
  }, [ativosVisiveisOrdenados, opcoes]);

  const handleAddDespesa = () => {
    if (bloquearEdicao) return;
    if (!novaDespesa) return;

    const opc = opcoes.find((o) => String(o.value) === String(novaDespesa));
    if (!opc) return;

    let descricao = opc.label;

    if (isTipoOutros(novaDespesa, opc.label)) {
      const desc = (novaDescricaoOutros || "").trim().slice(0, 100);
      if (!desc) {
        setErroNovaDescricaoOutros(true);
        return;
      }
      descricao = desc;
    }

    const nova = {
      idTipoDespesa: opc.value,
      descricao,
      valor: null,
      file: null,
      identificador: uuid(),
      deletar: false,
      collapsed: false,
      primeiraParcela: false,
      segundaParcela: false,
      nova: true,
    };

    setDespesas((prev) => [...prev, nova]);
    setNovaDespesa("");
    setNovaDescricaoOutros("");
    setErroNovaDescricaoOutros(false);
  };

  const handleRemoveById = async (identificador) => {
    if (bloquearEdicao || removingId) return;

    const target = despesas.find((it) => it.identificador === identificador);
    if (!target) return;

    const despesaId = target.id;
    const precisaApi =
      typeof onDeleteDespesa === "function" &&
      despesaId != null &&
      Number(despesaId) > 0;

    if (precisaApi) {
      try {
        setRemovingId(identificador);
        await onDeleteDespesa({ despesa: target });
        setDespesas((prev) =>
          prev.map((it) =>
            it.identificador === identificador
              ? { ...it, deletar: true, file: null }
              : it,
          ),
        );
        notify("Despesa removida com sucesso!", "success");
      } catch (err) {
        console.error("Erro ao excluir despesa:", err);
        notify(
          err?.message ||
            "Não foi possível remover a despesa. Tente novamente.",
        );
      } finally {
        setRemovingId(null);
      }
      return;
    }

    setDespesas((prev) =>
      prev.map((it) =>
        it.identificador === identificador
          ? { ...it, deletar: true, file: null }
          : it,
      ),
    );
  };

  const toggleCollapseById = (identificador) => {
    setDespesas((prev) =>
      prev.map((it) =>
        it.identificador === identificador
          ? { ...it, collapsed: !it.collapsed }
          : it,
      ),
    );
  };

  const isOutrosSelecionado = isTipoOutros(
    novaDespesa,
    opcoes.find((o) => String(o.value) === String(novaDespesa))?.label,
  );

  return (
    <Card>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center gap-2">
            <h5 className="fw-bold text-uppercase m-0">
              DESCRIÇÃO DAS DESPESAS
            </h5>
            <OverlayTrigger
              placement="right"
              overlay={
                <Tooltip id="tt-descricao-despesas">
                  Despesas do referido sindicato tais como aluguel, luz, água,
                  telefone, folha de pagamento, impostos, outros (referentes ao
                  exercício do ano corrente).
                </Tooltip>
              }
            >
              <span
                role="button"
                tabIndex={0}
                aria-label="Informações sobre a seção Descrição das Despesas"
              >
                <InfoCircleFill
                  size={16}
                  className="text-primary info-help-icon"
                />
              </span>
            </OverlayTrigger>
          </div>

          {!!ativosVisiveisOrdenados.length && (
            <div
              onClick={() =>
                setDespesas((prev) =>
                  prev.map((it) =>
                    it.deletar ? it : { ...it, collapsed: !tudoColapsado },
                  ),
                )
              }
              role="button"
              className="despesa-collapse-all text-primary"
            >
              {tudoColapsado ? (
                <>
                  <ArrowsExpand /> Expandir tudo
                </>
              ) : (
                <>
                  <ArrowsCollapse /> Colapsar tudo
                </>
              )}
            </div>
          )}
        </div>

        <div className="despesa-add-row">
          <div className="despesa-add-tipo">
            <Form.Label className="mb-1">Tipo*</Form.Label>
            <CustomSelect
              value={novaDespesa}
              options={opcoesParaAdicionar}
              onChange={(val) => setNovaDespesa(String(val))}
              placeholder="Selecione o tipo"
              disabled={bloquearEdicao}
              ariaLabel="Selecionar tipo de despesa"
              id="tipo-despesa-select"
            />
          </div>

          {isOutrosSelecionado && (
            <div className="despesa-add-outros">
              <Form.Label className="mb-1 d-flex justify-content-between">
                <span>Descrição*</span>
                <span className="small text-muted">
                  {novaDescricaoOutros.length}/100
                </span>
              </Form.Label>
              <Form.Control
                type="text"
                maxLength={100}
                placeholder="Descreva a despesa"
                value={novaDescricaoOutros}
                onChange={(e) => {
                  setNovaDescricaoOutros(e.target.value);
                  if (e.target.value.trim()) setErroNovaDescricaoOutros(false);
                }}
                isInvalid={erroNovaDescricaoOutros}
                disabled={bloquearEdicao}
              />
              <Form.Control.Feedback type="invalid">
                Informe a descrição.
              </Form.Control.Feedback>
            </div>
          )}

          <div className="despesa-add-action">
            <OverlayTrigger
              placement="top"
              overlay={
                bloquearEdicao ? (
                  <Tooltip id="tt-add-despesa-bloqueado">
                    Bloqueada enquanto sua solicitação está em análise.
                  </Tooltip>
                ) : (
                  <></>
                )
              }
            >
              <span className="d-inline-block">
                <Button
                  className={`btn-primario btn-acao ${
                    bloquearEdicao ? "btn-disabled" : ""
                  }`}
                  onClick={handleAddDespesa}
                  disabled={bloquearEdicao}
                >
                  Adicionar
                </Button>
              </span>
            </OverlayTrigger>
          </div>
        </div>
      </Card.Body>

      {ativosVisiveisOrdenados.length === 0 && ativos.length > 0 && (
        <Card.Body className="text-center text-muted py-3">
          Despesas desta etapa estão em análise. Você pode adicionar novas
          despesas para a próxima parcela.
        </Card.Body>
      )}

      {ativosVisiveisOrdenados.length > 0 && (
        <Card.Body className="mb-4">
          {ativosVisiveisOrdenados.map((it, i) => (
            <div key={it.identificador} className="mt-3">
              <Expense
                it={it}
                index={i}
                onRemove={() => handleRemoveById(it.identificador)}
                onToggleCollapse={() => toggleCollapseById(it.identificador)}
                setDespesas={setDespesas}
                isOutros={isTipoOutros(it.idTipoDespesa, it.descricao)}
                maxDescricaoLen={100}
                invalido={!!errorsDetalhe?.[it.identificador]}
                onCorrecaoChange={onCorrecaoChange}
                correcoes={correcoes}
                errosCorrecoes={errosCorrecoes}
                isSindicato={isSindicato}
                bloquearEdicao={bloquearEdicao}
                podeEditarItem={podeEditarItem}
                hasSolicitando={hasSolicitando}
                onUploadArquivo={onUploadArquivo}
                onNotify={notify}
                uploading={uploadingId === it.identificador}
                setUploadingId={setUploadingId}
                removing={removingId === it.identificador}
              />
            </div>
          ))}
        </Card.Body>
      )}

      <NotificationToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type={toastType}
      />
    </Card>
  );
}

function Expense({
  it,
  index,
  onRemove,
  onToggleCollapse,
  setDespesas,
  isOutros,
  maxDescricaoLen = 100,
  invalido = false,
  onCorrecaoChange,
  correcoes = {},
  errosCorrecoes = {},
  isSindicato = true,
  bloquearEdicao = false,
  podeEditarItem,
  hasSolicitando = false,
  onUploadArquivo = null,
  onNotify = null,
  uploading = false,
  setUploadingId = null,
  removing = false,
}) {
  const [downloading, setDownloading] = useState(false);
  const idComp = it.id || it.identificador;
  const isNovo = it?.nova === true;

  const bloqueado = !(
    isNovo ||
    (typeof podeEditarItem === "function"
      ? podeEditarItem(it)
      : !bloquearEdicao)
  );

  const handleBlurValor = () => {
    if (bloqueado) return;
    setDespesas((prev) =>
      prev.map((d) =>
        d.identificador === it.identificador
          ? { ...d, valor: d.valor ?? 0 }
          : d,
      ),
    );
  };

  const handleFileChange = async (e) => {
    if (bloqueado || uploading) return;
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const ext = String(file.name || "")
      .split(".")
      .pop()
      ?.toLowerCase();
    if (ext !== "pdf") {
      onNotify?.("Apenas arquivos PDF são permitidos.");
      return;
    }
    if (file.size > MAX_PDF_SIZE_BYTES) {
      onNotify?.(
        `O arquivo excede o limite de ${MAX_PDF_SIZE_MB} MB.`,
      );
      return;
    }

    if (typeof onUploadArquivo === "function") {
      try {
        setUploadingId?.(it.identificador);
        const result = await onUploadArquivo({ despesa: it, file });
        // Preferir IDs do POST; fallback para o que já veio do GET
        const despesaId =
          result?.despesaId ??
          result?.id ??
          result?.DespesaId ??
          result?.Id ??
          it.id;
        const nestedArquivo = result?.arquivo ?? result?.Arquivo;
        const arquivoIdRaw =
          result?.arquivoId ??
          result?.ArquivoId ??
          result?.idArquivo ??
          result?.IdArquivo ??
          nestedArquivo?.id ??
          nestedArquivo?.Id ??
          it.arquivoId ??
          null;
        const arquivoIdNum = Number(arquivoIdRaw);
        const arquivoId =
          arquivoIdRaw != null &&
          Number.isFinite(arquivoIdNum) &&
          arquivoIdNum > 0
            ? arquivoIdNum
            : null;
        const nomeArquivo =
          // Preferir o nome do arquivo escolhido pelo usuário (API pode devolver nome antigo/gerado).
          file.name ||
          result?.nomeArquivo ||
          result?.NomeArquivo ||
          "";
        const tipoDespesaId =
          result?.tipoDespesaId ??
          result?.TipoDespesaId ??
          it.idTipoDespesa;

        setDespesas((prev) =>
          prev.map((d) =>
            d.identificador === it.identificador
              ? {
                  ...d,
                  file,
                  nomeArquivo,
                  nomeComprovante: nomeArquivo,
                  nomeArquivoExistente: null,
                  id: despesaId ?? d.id,
                  arquivoId: arquivoId ?? d.arquivoId ?? null,
                  idTipoDespesa: tipoDespesaId ?? d.idTipoDespesa,
                }
              : d,
          ),
        );
        onNotify?.("Comprovante anexado com sucesso!", "success");
      } catch (err) {
        console.error("Erro ao enviar arquivo de despesa:", err);
        onNotify?.(
          err?.message ||
            "Não foi possível anexar o comprovante. Tente novamente.",
        );
      } finally {
        setUploadingId?.(null);
      }
      return;
    }

    setDespesas((prev) =>
      prev.map((d) =>
        d.identificador === it.identificador
          ? { ...d, file, nomeArquivoExistente: null }
          : d,
      ),
    );
  };

  const arquivoIdNumerico =
    it.arquivoId != null && Number(it.arquivoId) > 0
      ? Number(it.arquivoId)
      : null;
  const temArquivoLocal =
    typeof File !== "undefined" && it.file instanceof File;
  const isModoUploadV2 = typeof onUploadArquivo === "function";
  const podeBaixar = Boolean(
    arquivoIdNumerico ||
      temArquivoLocal ||
      (!isModoUploadV2 && (it.identificador || it.id)),
  );
  const nomeArquivoExibicao =
    (typeof File !== "undefined" && it.file instanceof File
      ? it.file.name
      : "") ||
    it.file?.name ||
    it.nomeComprovante ||
    it.nomeArquivo ||
    "";

  const handleDownloadComprovante = async () => {
    if (!podeBaixar) return;
    try {
      setDownloading(true);
      const nomeSugestao =
        nomeArquivoExibicao ||
        `comprovante_${arquivoIdNumerico || it.id || ""}.pdf`;

      // V2: sempre pelo id numérico do arquivo (nunca pelo GUID/identificador local)
      if (arquivoIdNumerico != null) {
        await downloadArquivoDespesaByArquivoId(arquivoIdNumerico, nomeSugestao);
        return;
      }

      // Arquivo acabou de ser anexado e ainda está só em memória
      if (temArquivoLocal) {
        const url = URL.createObjectURL(it.file);
        const a = document.createElement("a");
        a.href = url;
        a.download = nomeSugestao;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 0);
        return;
      }

      if (isModoUploadV2) {
        onNotify?.(
          "Arquivo ainda sem id. Anexe novamente ou recarregue a página.",
        );
        return;
      }

      // V1 legado: identificador persistido no backend
      const idDownload = it.identificador ?? it.id;
      if (idDownload == null) return;
      await downloadDespesaByIdentificador(idDownload, nomeSugestao);
    } catch (e) {
      console.error("Erro ao baixar comprovante:", e);
      onNotify?.("Não foi possível baixar o comprovante.");
    } finally {
      setDownloading(false);
    }
  };

  const hist = useMemo(() => {
    const arr = Array.isArray(it.historicoAnaliseAdequacaoDespesa)
      ? [...it.historicoAnaliseAdequacaoDespesa]
      : [];
    arr.sort((a, b) => {
      const ta = new Date(a?.dataSolicitacao || a?.dataCorrecao || 0).getTime();
      const tb = new Date(b?.dataSolicitacao || b?.dataCorrecao || 0).getTime();
      if (tb !== ta) return tb - ta;
      return (b?.id || 0) - (a?.id || 0);
    });
    return arr;
  }, [it.historicoAnaliseAdequacaoDespesa]);

  const pendente = useMemo(
    () =>
      (hist || []).find(
        (h) =>
          h?.dataSolicitacao != null &&
          String(h.dataSolicitacao).trim() !== "" &&
          h?.conforme === false &&
          (h?.dataCorrecao == null || h.dataCorrecao === ""),
      ) || null,
    [hist],
  );

  const visivel = !!pendente;

  const corr = correcoes[idComp] || {};

  const initialCorrecao = useMemo(
    () => ({
      solicitado: pendente?.dataSolicitacao || "",
      obsAnalista: pendente?.obsAnalista || "",
      obsSindicato: corr.obsSindicato ?? pendente?.obsSindicato ?? "",
    }),
    [
      pendente?.dataSolicitacao,
      pendente?.obsAnalista,
      pendente?.obsSindicato,
      corr.obsSindicato,
    ],
  );

  return (
    <div
      className={`expensive-item-container ${bloqueado ? "expense--locked" : ""}`}
    >
      <div className="expensive-item-header d-flex justify-content-between align-items-center">
        <h4 className="mb-0">{`${index + 1}. ${it.descricao}`}</h4>
        <div className="d-flex align-items-center gap-3">
          {it.collapsed && (
            <>
              <div>
                R{"$ "}
                {Number(it.valor || 0).toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                })}
              </div>
              <div className="d-flex align-items-center gap-1">
                <FilePdf className="file-pdf-icon" />{" "}
                {it.file?.name || it.nomeArquivo || it.nomeComprovante
                  ? "Comprovante anexado"
                  : "-"}
              </div>
            </>
          )}
          {it.collapsed ? (
            <ChevronDown
              onClick={onToggleCollapse}
              className="expensive-item-header-icon"
            />
          ) : (
            <ChevronUp
              onClick={onToggleCollapse}
              className="expensive-item-header-icon"
            />
          )}
        </div>
      </div>

      {!it.collapsed && (
        <div>
          <Form.Control
            id={`file-input-${it.identificador}`}
            type="file"
            accept=".pdf,application/pdf"
            style={{ display: "none" }}
            onChange={handleFileChange}
            disabled={bloqueado || uploading}
          />

          <div className="expensive-item-container-fields despesa-fields-row">
            <div className="despesa-field-valor">
              <Form.Label className="mb-1">Valor*</Form.Label>
              <NumericFormat
                thousandSeparator="."
                decimalSeparator=","
                prefix="R$ "
                placeholder="R$ 0,00"
                value={it.valor ?? ""}
                allowNegative={false}
                allowLeadingZeros={false}
                decimalScale={2}
                fixedDecimalScale
                className="form-control"
                onValueChange={(vals) => {
                  if (bloqueado) return;
                  const next =
                    vals.value === ""
                      ? null
                      : typeof vals.floatValue === "number"
                        ? vals.floatValue
                        : null;
                  setDespesas((prev) =>
                    prev.map((d) =>
                      d.identificador === it.identificador
                        ? { ...d, valor: next }
                        : d,
                    ),
                  );
                }}
                onBlur={handleBlurValor}
                inputMode="decimal"
                onFocus={(e) =>
                  bloqueado ? e.target.blur() : e.target.select()
                }
                onClick={(e) =>
                  bloqueado ? e.preventDefault() : e.target.select()
                }
                disabled={bloqueado}
              />
            </div>

            <div className="despesa-field-comprovante">
              <Form.Label className="mb-1">Comprovante*</Form.Label>
              {!bloqueado && !nomeArquivoExibicao ? (
                <div
                  className={`despesa-upload-zone despesa-upload-zone--inline ${invalido ? "is-invalid" : ""} ${uploading ? "is-uploading" : ""}`}
                >
                  <p className="despesa-upload-zone__text mb-0">
                    PDF, máx. <strong>{MAX_PDF_SIZE_MB} MB</strong>
                  </p>
                  <Button
                    type="button"
                    className="btn-primario btn-acao despesa-upload-zone__btn"
                    disabled={uploading}
                    onClick={() =>
                      document
                        .getElementById(`file-input-${it.identificador}`)
                        ?.click()
                    }
                  >
                    <FaFolderOpen className="me-2" />
                    {uploading ? "Enviando..." : "Escolher Arquivo"}
                  </Button>
                </div>
              ) : (
                <Form.Control
                  placeholder="Nenhum arquivo escolhido"
                  value={nomeArquivoExibicao}
                  disabled
                  readOnly
                  className={`file-name-input ${invalido ? "is-invalid" : ""}`}
                />
              )}
            </div>

            <div className="despesa-field-acoes">
              <Form.Label className="despesa-field-acoes__label mb-1">
                Ações
              </Form.Label>
              <div className="despesa-acoes-icons">
                {!!nomeArquivoExibicao && !bloqueado && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`tt-edit-${idComp}`}>
                        Trocar comprovante
                      </Tooltip>
                    }
                  >
                    <button
                      type="button"
                      className="despesa-icon-btn despesa-icon-btn--primary"
                      aria-label="Trocar comprovante"
                      disabled={uploading}
                      onClick={() =>
                        document
                          .getElementById(`file-input-${it.identificador}`)
                          ?.click()
                      }
                    >
                      <FaEdit />
                    </button>
                  </OverlayTrigger>
                )}

                {!!nomeArquivoExibicao && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      !podeBaixar ? (
                        <Tooltip id={`tt-arquivo-indisp-${idComp}`}>
                          Comprovante indisponível para download.
                        </Tooltip>
                      ) : (
                        <Tooltip id={`tt-download-${idComp}`}>
                          Baixar comprovante
                        </Tooltip>
                      )
                    }
                  >
                    <span>
                      <button
                        type="button"
                        className="despesa-icon-btn despesa-icon-btn--primary"
                        aria-label="Baixar comprovante"
                        onClick={handleDownloadComprovante}
                        disabled={!podeBaixar || downloading}
                      >
                        <FaDownload />
                      </button>
                    </span>
                  </OverlayTrigger>
                )}

                {!hasSolicitando && !bloqueado && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`tt-remove-${idComp}`}>
                        Remover despesa
                      </Tooltip>
                    }
                  >
                    <button
                      type="button"
                      className="despesa-icon-btn despesa-icon-btn--danger"
                      aria-label="Remover despesa"
                      onClick={onRemove}
                      disabled={removing || uploading}
                    >
                      <FaTrash />
                    </button>
                  </OverlayTrigger>
                )}
              </div>
            </div>
          </div>

          {visivel && (
            <Correcao
              key={`correcao-${idComp}`}
              idCampo={idComp}
              visible={visivel}
              isSindicato={isSindicato}
              erro={!!errosCorrecoes[idComp]}
              initialValue={initialCorrecao}
              onChange={(_, novoEstado) =>
                onCorrecaoChange?.(idComp, novoEstado)
              }
            />
          )}
        </div>
      )}
    </div>
  );
}

export default DescricaoDespesa;
